/**
 * 惠生正式商品主檔 TXT 解析器與核心驗證規則
 * 依據 2026 最新需求：
 * 1. 極簡寬容原則：僅在缺少 item_id 或缺少 cname 時判定該筆「無法匯入」，其餘情況（0價格、空白價格、無效GTIN、負庫存、缺分類、缺供應商、日期異常、-9999999999等）全數允許匯入並產生 WARNING。
 * 2. TSV 有限狀態機解析器 (RFC 4180 規範，支援 Tab、Quoted Multiline、CRLF、Escaped quotes)。
 * 3. 商品大分類由 mcs_no 第一碼推導 (A~P 映射)，mcs_no 作為子分類代碼，mcs_name 作為子分類名稱。
 * 4. 寬容日期解析 (支援 2026/6/16 10:03:16:29 等)，無法解析時保留 raw 值不阻擋匯入。
 * 5. 數字與價格正規化：trim(value), "" -> null, "0"/"0.000" -> 0, -9999999999 -> null, 負庫存保留。
 * 6. 重複商品判定 (9 種重複類型) 與身分防呆。
 */

// 惠生大分類代碼 (A~P) 標準繁體中文映射 (Section 四)
export const HUI_SHENG_CATEGORY_MAP: Record<string, string> = {
  A: '處方箋',
  B: 'OTC用藥',
  C: '甲類成藥（西藥）',
  D: '乙類成藥（西藥）',
  E: '甲類成藥（中藥）',
  F: '處方藥（中藥）',
  G: '乙類成藥（中藥）',
  H: '奶粉／液體營養配方',
  I: '尿布',
  J: '醫療器材',
  K: '美粧品（粧字碼）',
  L: '一般用品',
  M: '健康食品',
  N: '環境用藥',
  P: '惠市集',
};

export type DuplicateType =
  | 'EXACT_DUPLICATE'
  | 'SAME_PRODUCT_DIFFERENT_CODE'
  | 'SAME_PRODUCT_DIFFERENT_PACKAGE'
  | 'SAME_PRODUCT_DIFFERENT_VENDOR'
  | 'OLD_NEW_PRODUCT'
  | 'GIFT_SKU'
  | 'SPLIT_SKU'
  | 'POSSIBLE_DUPLICATE'
  | 'NOT_DUPLICATE';

export type PriceComparisonStatus =
  | 'ALL_SAME'
  | 'PURCHASE_PRICE_CHANGED'
  | 'AVERAGE_COST_CHANGED'
  | 'VIP_PRICE_CHANGED'
  | 'SALE_PRICE_CHANGED'
  | 'EMPLOYEE_PRICE_CHANGED'
  | 'MULTIPLE_PRICE_CHANGED'
  | 'PRICE_MISSING'
  | 'ZERO_PRICE_REVIEW'
  | 'PRICE_ABNORMAL';

export interface ParsedTxtRow {
  rowIndex: number;
  item_id: string; // 商品代號 (必須為 string / TEXT)
  cname: string; // 商品中文名稱
  ename: string; // 商品英文名稱
  normalized_product_name: string; // 正規化名稱 (專供比對/去重)
  mea: string; // 銷售單位
  mcs_no: string; // 商品子分類代碼 (如 B22, M31)
  mcs_name: string; // 商品子分類名稱 (如 一般皮膚炎)
  category_code: string | null; // 商品大分類代碼 (由 mcs_no 第一碼推導)
  category_name: string | null; // 商品大分類名稱 (查表 A~P)
  subcategory_code: string | null; // 商品子分類代碼
  subcategory_name: string | null; // 商品子分類名稱
  cust_id: string; // 供應商代碼
  sname: string; // 供應商名稱
  item_iid: string; // 主條碼候選 (原始值)
  item_id2: string; // 次條碼候選 (原始值)
  stock: number; // 總庫存 (支援負庫存)
  on_order: number; // 已訂未到
  sa_price: number | null; // 一般售價
  avg_cost: number | null; // 平均成本
  in_price: number | null; // 目前進價 (固定使用 in_price，不擅自用 avg_cost 覆蓋)
  vip_price: number | null; // VIP 價
  emp_price: number | null; // 員工價
  return_yn: string; // 是否可退 (Y/N)
  updtime: string; // 來源最後更新時間 (原始或標準化)
  source_date_raw?: string; // 來源原始日期文字
  valid_barcodes: string[]; // 符合 GTIN 規範的國際條碼
  record_type: 'PRODUCT' | 'FEE' | 'PREPAYMENT' | 'SERVICE' | 'VIRTUAL' | 'NEEDS_REVIEW';
  status_hint: 'ACTIVE' | 'INACTIVE'; // (停) 等標記
  detected_giveaway_candidate: string | null; // 4+1, 買4送1 等候選
  data_quality_issues: string[];
  source_row_hash: string; // 來源列 Hash
  raw_data: Record<string, string>; // 原始所有欄位保存
}

export interface InvalidTxtRow {
  rowNumber: number;
  itemId?: string;
  cname?: string;
  reason: string;
}

export interface TxtParseResult {
  totalRows: number;
  importableRows: number;
  warningRows: number;
  invalidRowsCount: number;
  validRows: ParsedTxtRow[];
  invalidRows: InvalidTxtRow[];
  headerColumns: string[];
  detectedDelimiter: string;
  encoding: string;
  missingRequiredHeaders?: string[];
  summary: {
    productCount: number;
    nonProductCount: number;
    qualityIssueCount: number;
    zeroInPriceCount: number;
    invalidBarcodeCount: number;
    inactiveCount: number;
  };
}

/**
 * TSV / CSV 有限狀態機解析器 (RFC 4180 規範，支援 Tab、Quoted Multiline 欄位、CRLF 與 Escaped Quotes)
 * 嚴禁使用 text.split("\n") 或 line.split("\t")
 */
export function parseTsv(textContent: string, delimiter = '\t'): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let insideQuotes = false;

  const len = textContent.length;
  let i = 0;

  while (i < len) {
    const char = textContent[i];

    if (insideQuotes) {
      if (char === '"') {
        // 檢查是否為跳脫引號 ""
        if (i + 1 < len && textContent[i + 1] === '"') {
          currentField += '"';
          i += 2;
          continue;
        } else {
          // 閉合雙引號
          insideQuotes = false;
          i++;
          continue;
        }
      } else {
        // 引號內文字（包含換行符號 \n, \r 與 Tab）
        currentField += char;
        i++;
        continue;
      }
    } else {
      if (char === '"') {
        insideQuotes = true;
        i++;
        continue;
      }

      if (char === delimiter) {
        currentRow.push(currentField);
        currentField = '';
        i++;
        continue;
      }

      if (char === '\r') {
        if (i + 1 < len && textContent[i + 1] === '\n') {
          i++; // 略過 CRLF 的 \n
        }
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
        i++;
        continue;
      }

      if (char === '\n') {
        currentRow.push(currentField);
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
        i++;
        continue;
      }

      currentField += char;
      i++;
    }
  }

  // 處理結尾欄位
  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField);
    rows.push(currentRow);
  }

  return rows;
}

/**
 * 條碼 GTIN 檢核 (8, 12, 13, 14 碼 Check Digit 檢查)
 */
export function isValidGtin(barcode: string): boolean {
  if (!barcode) return false;
  const clean = barcode.trim();
  if (!/^\d{8}$|^\d{12,14}$/.test(clean)) return false;

  // 全 0 視為無效條碼
  if (/^0+$/.test(clean)) return false;

  const len = clean.length;
  let sum = 0;
  for (let i = 0; i < len - 1; i++) {
    const digit = parseInt(clean[i]!, 10);
    const weight = (len - 1 - i) % 2 === 1 ? 3 : 1;
    sum += digit * weight;
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit === parseInt(clean[len - 1]!, 10);
}

/**
 * 商品品名正規化 (專供去重、相似度比對與搜尋索引)
 */
export function normalizeProductName(name: string): string {
  if (!name) return '';
  let norm = name;

  // 1. 全形轉半形
  norm = norm.replace(/[\uff01-\uff5e]/g, (ch) =>
    String.fromCharCode(ch.charCodeAt(0) - 0xfee0),
  );
  norm = norm.replace(/\u3000/g, ' '); // 全形空格

  // 2. 轉小寫
  norm = norm.toLowerCase();

  // 3. 去除常見括號符號但保留內容，去除 (停) 標籤
  norm = norm.replace(/\(停\)|（停）|\[停\]/g, ' ');
  norm = norm.replace(/[【】[\]()（）<>《》「」『』\-_~*#]/g, ' ');

  // 4. 單位與容量規格正規化
  norm = norm.replace(/\bml\b|\b毫升\b|\bcc\b/g, 'ml');
  norm = norm.replace(/\bg\b|\b克\b|\b公克\b/g, 'g');
  norm = norm.replace(/\bkg\b|\b公斤\b/g, 'kg');
  norm = norm.replace(/\bl\b|\b公升\b/g, 'l');
  norm = norm.replace(/\b錠\b|\b顆\b|\b粒\b|\b膠囊\b/g, '粒');

  // 5. 連續空白合併
  norm = norm.replace(/\s+/g, ' ').trim();

  return norm;
}

/**
 * 寬容數字正規化 (Section 十二)
 * - "" / null / undefined -> null
 * - "0" / "0.000" -> 0
 * - 負數 (如 stock = -7) -> -7
 */
export function normalizeNumber(val: string | number | undefined | null): number | null {
  if (val === undefined || val === null) return null;
  const str = String(val).trim().replace(/,/g, '');
  if (str === '' || str === 'NULL' || str === 'null') return null;
  const num = typeof val === 'number' ? val : parseFloat(str);
  if (isNaN(num)) return null;
  return num;
}

/**
 * 清理價格：過濾占位無效值（如 -9999999999.000 或負價格） (Section 十三)
 * - -9999999999 自動轉 NULL (不報錯)
 * - 0 正常保留為 0
 */
export function sanitizePrice(val: string | number | undefined | null): number | null {
  if (val === undefined || val === null) return null;
  const str = String(val).trim().replace(/,/g, '');
  if (str === '' || str === 'NULL' || str === 'null') return null;
  const num = typeof val === 'number' ? val : parseFloat(str);
  if (isNaN(num)) return null;
  if (num <= -999999 || num === -9999999999) return null; // -9999999999 轉為 NULL
  if (num < 0) return null; // 負價格轉為 NULL
  return Math.round(num * 100) / 100;
}

/**
 * 寬容日期時間解析 (Section 十一)
 * 支援格式：
 * - 2026/6/16 10:03:16:29
 * - 2025/12/22 08:45:03:553
 * - 2021/5/1 20:48:10
 * - 2026-08-01 10:00:00
 * - 20260801
 * 解析失敗時保存 source_date_raw，正式日期可為 null，不阻止匯入。
 */
export function parseLegacyDateTime(str: string | undefined | null): { iso: string | null; raw: string } {
  if (!str || !str.trim()) return { iso: null, raw: '' };
  const raw = str.trim();

  // 1. 標準或含多冒號毫秒 (HH:mm:ss:ms -> HH:mm:ss.ms)
  const normalized = raw
    .replace(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/, '$1-$2-$3')
    .replace(/(\d{1,2}):(\d{2}):(\d{2}):(\d+)/, '$1:$2:$3.$4');

  const d = new Date(normalized);
  if (!isNaN(d.getTime())) {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return { iso: `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`, raw };
  }

  // 2. YYYY/MM/DD 或 YYYY-MM-DD
  const m = raw.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})/);
  if (m && m[1] && m[2] && m[3]) {
    const yyyy = m[1];
    const mm = m[2].padStart(2, '0');
    const dd = m[3].padStart(2, '0');
    return { iso: `${yyyy}-${mm}-${dd} 00:00:00`, raw };
  }

  // 3. 純 8 碼數字 YYYYMMDD
  const m8 = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m8 && m8[1] && m8[2] && m8[3]) {
    return { iso: `${m8[1]}-${m8[2]}-${m8[3]} 00:00:00`, raw };
  }

  // 解析不了時，保留原始字串，正式日期傳 NULL
  return { iso: null, raw };
}

/**
 * 簡易字串 Hash (FNV-1a / Murmur3 概念，免依賴 Node crypto)
 */
export function simpleStringHash(str: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * 商品大分類推導：依 mcs_no 第一碼推導 (Section 四 & 五)
 */
export function deriveCategoryFromMcsNo(mcsNo: string | null | undefined): {
  categoryCode: string | null;
  categoryName: string | null;
  subcategoryCode: string | null;
  subcategoryName: string | null;
} {
  if (!mcsNo || !mcsNo.trim()) {
    return {
      categoryCode: null,
      categoryName: null,
      subcategoryCode: null,
      subcategoryName: null,
    };
  }

  const cleanMcsNo = mcsNo.trim();
  const firstChar = cleanMcsNo.charAt(0).toUpperCase();
  const categoryName = HUI_SHENG_CATEGORY_MAP[firstChar] || firstChar;

  return {
    categoryCode: firstChar,
    categoryName,
    subcategoryCode: cleanMcsNo,
    subcategoryName: null, // 由 mcs_name 傳入
  };
}

/**
 * 判斷商品記錄類型（排除預收貨款、費用、服務等非實體商品） (Section 六)
 */
export function detectRecordType(
  cname: string,
  mcs_name: string,
  item_id: string,
): 'PRODUCT' | 'FEE' | 'PREPAYMENT' | 'SERVICE' | 'VIRTUAL' | 'NEEDS_REVIEW' {
  const name = (cname || '').toLowerCase();
  const cat = (mcs_name || '').toLowerCase();

  if (/預收|訂金|保證金/i.test(name) || /預收/i.test(cat)) {
    return 'PREPAYMENT';
  }
  if (/運費|手續費|服務費|工本費|規費|折讓/i.test(name) || /費用/i.test(cat)) {
    return 'FEE';
  }
  if (/諮詢|檢測|清潔|配送|包裝服務/i.test(name) || /服務/i.test(cat)) {
    return 'SERVICE';
  }
  if (/點數|禮券|電子券|兌換券|虛擬/i.test(name)) {
    return 'VIRTUAL';
  }
  if (!name || name === '未命名' || item_id.startsWith('TEMP')) {
    return 'NEEDS_REVIEW';
  }
  return 'PRODUCT';
}

/**
 * 偵測 Legacy 備註中之搭贈候選字串 (Section 二十五)
 */
export function detectGiveawayFromText(text: string): string | null {
  if (!text) return null;
  const match = text.match(/(\d+[+＋送]\d+|買\d+送\d+|買\d+贈\d+)/i);
  return match ? match[1] ?? null : null;
}

/**
 * 檢查商品代號身分重大衝突 (Section 十)
 * 條件：item_id 相同，但商品名稱完全不同 + 條碼也完全不同
 */
export function checkSkuIdentityConflict(
  existing: {
    product_name: string;
    primary_barcode_candidate?: string | null;
    additional_barcode_candidate?: string | null;
    barcode_list_json?: string | null;
  },
  incoming: {
    cname: string;
    valid_barcodes: string[];
    item_iid?: string;
    item_id2?: string;
  },
): boolean {
  const normExist = normalizeProductName(existing.product_name);
  const normIncoming = normalizeProductName(incoming.cname);

  // 1. 名稱相似度檢驗：若字首與關鍵字皆不匹配
  const nameMatch =
    normExist === normIncoming ||
    normExist.includes(normIncoming) ||
    normIncoming.includes(normExist);

  // 2. 條碼重疊檢驗
  let existBarcodes: string[] = [];
  try {
    if (existing.barcode_list_json) {
      existBarcodes = JSON.parse(existing.barcode_list_json);
    }
  } catch {
    // ignore parse error
  }
  if (existing.primary_barcode_candidate) existBarcodes.push(existing.primary_barcode_candidate);
  if (existing.additional_barcode_candidate)
    existBarcodes.push(existing.additional_barcode_candidate);

  const incomingBarcodes = [
    ...incoming.valid_barcodes,
    incoming.item_iid,
    incoming.item_id2,
  ].filter(Boolean) as string[];

  const barcodeMatch = existBarcodes.some((b) => incomingBarcodes.includes(b));

  // 若品名不同且條碼也完全不重疊，視為身分衝突
  if (!nameMatch && !barcodeMatch && existBarcodes.length > 0 && incomingBarcodes.length > 0) {
    return true;
  }
  return false;
}

/**
 * 價格異動 Decimal 精度比對 (Section 十四 ~ 十八)
 * <= 5% 正常
 * 5% ~ 20% 提醒
 * > 20% 高風險
 */
export function evaluatePriceChange(
  oldPrice: number | null | undefined,
  newPrice: number | null | undefined,
): {
  isChanged: boolean;
  diffAmount: number;
  diffPercent: number;
  isHighRisk: boolean;
  isWarning: boolean;
} {
  const p1 = oldPrice ?? null;
  const p2 = newPrice ?? null;

  if (p1 === p2 || (p1 === null && p2 === null)) {
    return { isChanged: false, diffAmount: 0, diffPercent: 0, isHighRisk: false, isWarning: false };
  }

  if (p1 === null || p2 === null) {
    return {
      isChanged: true,
      diffAmount: p2 !== null ? p2 : -(p1 || 0),
      diffPercent: 100,
      isHighRisk: false,
      isWarning: true,
    };
  }

  // Decimal 精度 100 與 100.000 視為相同
  const d1 = Math.round(p1 * 100) / 100;
  const d2 = Math.round(p2 * 100) / 100;

  if (d1 === d2) {
    return { isChanged: false, diffAmount: 0, diffPercent: 0, isHighRisk: false, isWarning: false };
  }

  const diffAmount = Math.round((d2 - d1) * 100) / 100;
  const diffPercent = d1 > 0 ? Math.round(Math.abs(diffAmount / d1) * 1000) / 10 : 100;

  const isHighRisk = diffPercent > 20; // > 20% 高風險
  const isWarning = diffPercent > 5 && diffPercent <= 20; // 5~20% 提醒

  return { isChanged: true, diffAmount, diffPercent, isHighRisk, isWarning };
}

/**
 * 跨 item_id 9 種重複商品類型偵測 (Section 二十)
 */
export function detectDuplicateType(
  prodA: {
    item_id: string;
    cname: string;
    mea?: string;
    valid_barcodes: string[];
    cust_id?: string;
    in_price?: number | null;
    vip_price?: number | null;
  },
  prodB: {
    item_id: string;
    cname: string;
    mea?: string;
    valid_barcodes: string[];
    cust_id?: string;
    in_price?: number | null;
    vip_price?: number | null;
  },
): {
  duplicateType: DuplicateType;
  similarityScore: number;
  matchReasons: string[];
} {
  const reasons: string[] = [];
  let score = 0;

  const normA = normalizeProductName(prodA.cname);
  const normB = normalizeProductName(prodB.cname);

  // 1. 合法 GTIN 條碼比對 (最嚴格)
  const commonBarcodes = prodA.valid_barcodes.filter(
    (b) => prodB.valid_barcodes.includes(b) && isValidGtin(b),
  );
  if (commonBarcodes.length > 0) {
    score += 50;
    reasons.push(`國際條碼重疊 (${commonBarcodes.join(', ')})`);
  }

  // 2. 名稱比對
  const baseA = normA.replace(/\d+\s*(粒|ml|g|kg|l|包|片|支|卷|個|入|盒|瓶|罐|管|袋)\S*/g, '').trim();
  const baseB = normB.replace(/\d+\s*(粒|ml|g|kg|l|包|片|支|卷|個|入|盒|瓶|罐|管|袋)\S*/g, '').trim();

  if (normA === normB && normA.length > 0) {
    score += 40;
    reasons.push('商品正規化品名完全一致');
  } else if (baseA.length > 2 && baseA === baseB) {
    score += 35;
    reasons.push(`核心主商品名稱一致 (${baseA})`);
  } else if (normA.includes(normB) || normB.includes(normA)) {
    score += 25;
    reasons.push('商品名稱高度相似');
  } else if (baseA.length > 3 && (baseA.includes(baseB) || baseB.includes(baseA))) {
    score += 20;
    reasons.push('商品核心名稱部分重疊');
  }

  // 3. 單位比對
  if (prodA.mea && prodB.mea && prodA.mea === prodB.mea) {
    score += 10;
    reasons.push(`銷售單位一致 (${prodA.mea})`);
  } else if (prodA.mea && prodB.mea && prodA.mea !== prodB.mea) {
    reasons.push(`包裝單位不同 (${prodA.mea} vs ${prodB.mea})`);
  }

  // 4. 供應商比對
  const sameVendor = prodA.cust_id && prodB.cust_id && prodA.cust_id === prodB.cust_id;
  if (sameVendor) {
    score += 10;
    reasons.push('供應商一致');
  }

  // 判定類型
  if (prodA.cname.includes('(停)') || prodB.cname.includes('(停)')) {
    return { duplicateType: 'OLD_NEW_PRODUCT', similarityScore: Math.min(100, score), matchReasons: reasons };
  }

  if (/贈品|搭贈|非賣品/i.test(prodA.cname) || /贈品|搭贈|非賣品/i.test(prodB.cname) || prodA.in_price === 0 || prodB.in_price === 0) {
    return { duplicateType: 'GIFT_SKU', similarityScore: Math.min(100, score), matchReasons: reasons };
  }

  if (/單顆|散裝|拆賣|單包|分裝/i.test(prodA.cname) || /單顆|散裝|拆賣|單包|分裝/i.test(prodB.cname)) {
    return { duplicateType: 'SPLIT_SKU', similarityScore: Math.min(100, score), matchReasons: reasons };
  }

  if (baseA.length > 2 && baseA === baseB && (prodA.mea !== prodB.mea || normA !== normB)) {
    return { duplicateType: 'SAME_PRODUCT_DIFFERENT_PACKAGE', similarityScore: Math.min(100, score + 30), matchReasons: reasons };
  }

  if (score >= 90) {
    if (prodA.cust_id && prodB.cust_id && prodA.cust_id !== prodB.cust_id) {
      return { duplicateType: 'SAME_PRODUCT_DIFFERENT_VENDOR', similarityScore: score, matchReasons: reasons };
    }
    return { duplicateType: 'SAME_PRODUCT_DIFFERENT_CODE', similarityScore: score, matchReasons: reasons };
  }

  if (score >= 60) {
    if (prodA.mea !== prodB.mea || /入|盒|瓶|罐|包/i.test(prodA.cname + prodB.cname)) {
      return { duplicateType: 'SAME_PRODUCT_DIFFERENT_PACKAGE', similarityScore: score, matchReasons: reasons };
    }
    if (prodA.cust_id && prodB.cust_id && prodA.cust_id !== prodB.cust_id) {
      return { duplicateType: 'SAME_PRODUCT_DIFFERENT_VENDOR', similarityScore: score, matchReasons: reasons };
    }
    return { duplicateType: 'POSSIBLE_DUPLICATE', similarityScore: score, matchReasons: reasons };
  }

  return { duplicateType: 'NOT_DUPLICATE', similarityScore: score, matchReasons: reasons };
}

/**
 * 主入口：解析惠生正式商品主檔 TXT (寬容狀態機解析與異常收集)
 */
export function parseProductMasterTxt(
  textContent: string,
  options?: { encoding?: string },
): TxtParseResult {
  const rawRows = parseTsv(textContent, '\t');

  if (rawRows.length === 0) {
    return {
      totalRows: 0,
      importableRows: 0,
      warningRows: 0,
      invalidRowsCount: 0,
      validRows: [],
      invalidRows: [],
      headerColumns: [],
      detectedDelimiter: '\t',
      encoding: options?.encoding || 'CP950',
      summary: {
        productCount: 0,
        nonProductCount: 0,
        qualityIssueCount: 0,
        zeroInPriceCount: 0,
        invalidBarcodeCount: 0,
        inactiveCount: 0,
      },
    };
  }

  // 第一列為 Header
  const headerRow = rawRows[0] ?? [];
  const headers = headerRow.map((h) => h.trim().toLowerCase());

  // 建立欄位名稱至 index 的 mapping
  const getColIndex = (...aliases: string[]) => {
    for (const a of aliases) {
      const lower = a.toLowerCase();
      const idx = headers.findIndex((h) => h === lower);
      if (idx !== -1) return idx;
    }
    for (const a of aliases) {
      const lower = a.toLowerCase();
      const idx = headers.findIndex((h) => h.includes(lower));
      if (idx !== -1) return idx;
    }
    return -1;
  };

  const idxItemId = getColIndex('item_id', 'itemid', 'item_no', 'itemno', 'product_code', '商品代號', '商品編號', '貨號');
  const idxCname = getColIndex('cname', 'product_name', 'name_zh', '品名', '商品名稱', '中文品名');
  const idxEname = getColIndex('ename', 'english_name', 'name_en', '英文品名', '英文名稱');
  const idxMea = getColIndex('mea', 'unit', 'sales_unit', '單位', '銷售單位', '包裝單位');
  const idxMcsNo = getColIndex('mcs_no', 'subcategory_code', '商品子分類代碼', '分類代號', '子分類代號');
  const idxMcsName = getColIndex('mcs_name', 'subcategory_name', '商品子分類名稱', '分類名稱', '子分類名稱');
  const idxCustId = getColIndex('cust_id', 'vendor_code', 'vendor_id', '廠商代號', '供應商代號');
  const idxSname = getColIndex('sname', 'vendor_name', 'supplier_name', '廠商名稱', '供應商名稱', '廠商簡稱');
  const idxItemIid = getColIndex('item_iid', 'barcode', 'gtin', 'primary_barcode', '國際條碼', '主條碼');
  const idxItemId2 = getColIndex('item_id2', 'secondary_barcode', '次條碼', '內部條碼');
  const idxStock = getColIndex('stock', 'warehouse_stock', '庫存', '現有庫存', '總庫存');
  const idxOnOrder = getColIndex('on_order', 'on_order_quantity', '在途', '在途量', '已訂未到');
  const idxSaPrice = getColIndex('sa_price', 'sale_price', 'regular_sale_price', '售價', '零售價', '一般售價');
  const idxAvgCost = getColIndex('avg_cost', 'average_cost', '平均成本', '均成');
  const idxInPrice = getColIndex('in_price', 'purchase_price', 'current_purchase_price', '進價', '牌價進價', '目前進價');
  const idxVipPrice = getColIndex('vip_price', 'vip', '會員價', 'vip價');
  const idxEmpPrice = getColIndex('emp_price', 'employee_price', '員工價');
  const idxReturnYn = getColIndex('return_yn', 'return_allowed', '是否可退', '退貨');
  const idxUpdtime = getColIndex('updtime', 'source_updated_at', 'updated_at', '最後更新時間', '更新時間');

  // 僅在缺少 item_id 或 cname 時判定缺少必要 Header (Section 六 & 三十五)
  const missingHeaders: string[] = [];
  if (idxItemId === -1) missingHeaders.push('item_id (商品代號)');
  if (idxCname === -1) missingHeaders.push('cname (商品名稱)');

  if (missingHeaders.length > 0) {
    return {
      totalRows: Math.max(0, rawRows.length - 1),
      importableRows: 0,
      warningRows: 0,
      invalidRowsCount: Math.max(0, rawRows.length - 1),
      validRows: [],
      invalidRows: [],
      headerColumns: headerRow,
      detectedDelimiter: '\t',
      encoding: options?.encoding || 'CP950',
      missingRequiredHeaders: missingHeaders,
      summary: {
        productCount: 0,
        nonProductCount: 0,
        qualityIssueCount: 0,
        zeroInPriceCount: 0,
        invalidBarcodeCount: 0,
        inactiveCount: 0,
      },
    };
  }

  const validRows: ParsedTxtRow[] = [];
  const invalidRows: InvalidTxtRow[] = [];
  let productCount = 0;
  let nonProductCount = 0;
  let zeroInPriceCount = 0;
  let invalidBarcodeCount = 0;
  let qualityIssueCount = 0;
  let inactiveCount = 0;
  let totalWarningRows = 0;

  for (let i = 1; i < rawRows.length; i++) {
    const cols = rawRows[i] ?? [];
    const rowNumber = i + 1; // 1-based index (含 Header 為第 1 列)

    if (cols.length === 0 || (cols.length === 1 && !cols[0]?.trim())) {
      continue; // 略過純空白行
    }

    const getValue = (idx: number) =>
      idx >= 0 && idx < cols.length ? (cols[idx] ?? '').trim() : '';

    const itemId = getValue(idxItemId);
    const cname = getValue(idxCname);

    // 真正可以阻止單筆商品匯入的條件只限：缺少 item_id 或缺少 cname (Section 六)
    if (!itemId && !cname) {
      invalidRows.push({
        rowNumber,
        itemId: '',
        cname: '',
        reason: '缺少商品代號 (item_id) 與商品名稱 (cname)',
      });
      continue;
    }
    if (!itemId) {
      invalidRows.push({
        rowNumber,
        itemId: '',
        cname,
        reason: '缺少商品代號 (item_id)',
      });
      continue;
    }
    if (!cname) {
      invalidRows.push({
        rowNumber,
        itemId,
        cname: '',
        reason: '缺少商品名稱 (cname)',
      });
      continue;
    }

    // 取得其餘欄位
    const ename = getValue(idxEname);
    const mea = getValue(idxMea);
    const mcsNo = getValue(idxMcsNo);
    const mcsName = getValue(idxMcsName);
    const custId = getValue(idxCustId);
    const sname = getValue(idxSname);
    const itemIid = getValue(idxItemIid);
    const itemId2 = getValue(idxItemId2);
    const stock = normalizeNumber(getValue(idxStock)) ?? 0;
    const onOrder = normalizeNumber(getValue(idxOnOrder)) ?? 0;

    const saPrice = sanitizePrice(getValue(idxSaPrice));
    const avgCost = sanitizePrice(getValue(idxAvgCost));
    const inPrice = sanitizePrice(getValue(idxInPrice));
    const vipPrice = sanitizePrice(getValue(idxVipPrice));
    const empPrice = sanitizePrice(getValue(idxEmpPrice));
    const returnYn = getValue(idxReturnYn).toUpperCase() || 'Y';
    const parsedDate = parseLegacyDateTime(getValue(idxUpdtime));
    const updtime = parsedDate.iso || '';

    // 商品大分類推導 (Section 九)
    const catDerived = deriveCategoryFromMcsNo(mcsNo);
    const categoryCode = catDerived.categoryCode;
    const categoryName = catDerived.categoryName;
    const subcategoryCode = mcsNo ? mcsNo.trim() : null;
    const subcategoryName = mcsName ? mcsName.trim() : null;

    // 原始所有欄位完整保存 (Section 七)
    const rawData: Record<string, string> = {};
    headers.forEach((header, colIdx) => {
      if (colIdx < cols.length) {
        rawData[header] = (cols[colIdx] ?? '').trim();
      }
    });

    // 條碼 GTIN 檢核：只有合法 GTIN 才進入正式條碼清單，但非 GTIN 條碼仍保存並產生 Warning (Section 八)
    const validBarcodes: string[] = [];
    const candidateBarcodes = [itemIid, itemId2];
    for (const b of candidateBarcodes) {
      if (b && isValidGtin(b) && !validBarcodes.includes(b)) {
        validBarcodes.push(b);
      }
    }

    // 判斷商品記錄類型
    const recordType = detectRecordType(cname, mcsName, itemId);
    if (recordType === 'PRODUCT') productCount++;
    else nonProductCount++;

    // 判斷狀態提示 (停)
    const statusHint = /\(停\)|（停）|\[停\]/i.test(cname) ? 'INACTIVE' : 'ACTIVE';
    if (statusHint === 'INACTIVE') inactiveCount++;

    // 偵測搭贈候選字串 (4+1, 買4送1 等)
    const detectedGiveaway = detectGiveawayFromText(cname) || detectGiveawayFromText(getValue(getColIndex('ubc1')));

    // 資料品質警示檢查 (僅標記 Warning，絕不阻止商品匯入)
    const qualityIssues: string[] = [];
    if (inPrice === null || inPrice === 0) {
      qualityIssues.push('目前進價為0或未設定');
      zeroInPriceCount++;
    }
    if (!categoryCode) {
      qualityIssues.push('商品分類待確認');
    }
    if ((itemIid || itemId2) && validBarcodes.length === 0) {
      qualityIssues.push('條碼待確認 (非標準GTIN規格)');
      invalidBarcodeCount++;
    }
    if (inPrice && vipPrice && inPrice > vipPrice) {
      qualityIssues.push('進價高於VIP售價');
    }
    if (inPrice && saPrice && inPrice > saPrice) {
      qualityIssues.push('進價高於一般售價');
    }
    if (stock < 0) {
      qualityIssues.push(`負庫存 (${stock})`);
    }
    if (!parsedDate.iso && parsedDate.raw) {
      qualityIssues.push('更新時間非標準格式');
    }
    if (recordType !== 'PRODUCT') {
      qualityIssues.push(`非實體商品(${recordType})`);
    }

    if (qualityIssues.length > 0) {
      qualityIssueCount++;
      totalWarningRows++;
    }

    // 計算 Row Hash
    const sourceHashInput = `${itemId}|${cname}|${mcsNo}|${custId}|${inPrice}|${avgCost}|${vipPrice}|${saPrice}|${empPrice}|${stock}|${onOrder}|${returnYn}|${updtime}`;
    const sourceRowHash = simpleStringHash(sourceHashInput);

    validRows.push({
      rowIndex: i,
      item_id: itemId,
      cname,
      ename,
      normalized_product_name: normalizeProductName(cname),
      mea,
      mcs_no: mcsNo,
      mcs_name: mcsName,
      category_code: categoryCode,
      category_name: categoryName,
      subcategory_code: subcategoryCode,
      subcategory_name: subcategoryName,
      cust_id: custId,
      sname,
      item_iid: itemIid,
      item_id2: itemId2,
      stock,
      on_order: onOrder,
      sa_price: saPrice,
      avg_cost: avgCost,
      in_price: inPrice,
      vip_price: vipPrice,
      emp_price: empPrice,
      return_yn: returnYn,
      updtime,
      source_date_raw: parsedDate.raw,
      valid_barcodes: validBarcodes,
      record_type: recordType,
      status_hint: statusHint,
      detected_giveaway_candidate: detectedGiveaway,
      data_quality_issues: qualityIssues,
      source_row_hash: sourceRowHash,
      raw_data: rawData,
    });
  }

  const totalRowsCount = validRows.length + invalidRows.length;

  return {
    totalRows: totalRowsCount,
    importableRows: validRows.length,
    warningRows: totalWarningRows,
    invalidRowsCount: invalidRows.length,
    validRows,
    invalidRows,
    headerColumns: headerRow,
    detectedDelimiter: '\t',
    encoding: options?.encoding || 'CP950',
    summary: {
      productCount,
      nonProductCount,
      qualityIssueCount,
      zeroInPriceCount,
      invalidBarcodeCount,
      inactiveCount,
    },
  };
}
